import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, Drawer, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdBlock, MdCheckCircle, MdClose, MdDeleteOutline, MdDownload, MdEdit, MdInventory2, MdRefresh, MdSearch, MdUpload, MdVisibility } from 'react-icons/md';
import { FiFilter } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteData, getData, putData } from '../../Webservice/Webservice';
import { API_URL } from '../../Utils/url';
import { FONT, StatusChip, authToken, img, inr, stockOf, yellowBtn } from '../../components/Products/productShared';

const LIMIT = 15;
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const EMPTY = { status: '', categoryId: '', subcategoryId: '', isPublished: '', stockStatus: '', ownership: '', brand: '', upc: '', hsnCode: '', minPrice: '', maxPrice: '', sort: 'createdAt:desc' };
const SORTS = [['createdAt:desc', 'Newest first'], ['createdAt:asc', 'Oldest first'], ['title:asc', 'Name A–Z'], ['title:desc', 'Name Z–A'], ['price:asc', 'Price low to high'], ['price:desc', 'Price high to low'], ['stock:asc', 'Stock low to high'], ['stock:desc', 'Stock high to low']];

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 140, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const download = async (path, filename) => {
  const res = await fetch(`${API_URL}${path}`, { headers: { Authorization: authToken() } });
  if (!res.ok) throw new Error('The download failed');
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// the dashboard links here with ?status=…&stockStatus=… and so on
const fromUrl = (search) => {
  const q = new URLSearchParams(search);
  const own = q.get('ownership') || '';
  return {
    search: q.get('title') || q.get('search') || '',
    filters: {
      ...EMPTY,
      status: q.get('status') && q.get('status') !== 'all' ? q.get('status') : '',
      categoryId: q.get('categoryId') || q.get('category') || '',
      subcategoryId: q.get('subcategoryId') || '',
      isPublished: q.get('isPublished') && q.get('isPublished') !== 'all' ? q.get('isPublished') : '',
      stockStatus: q.get('stockStatus') && q.get('stockStatus') !== 'all' ? q.get('stockStatus') : '',
      ownership: own === 'own' ? 'Own Product' : own === 'seller' || own === 'vendor' ? 'Seller Product' : own === 'all' ? '' : own,
      upc: q.get('upc') || '',
      hsnCode: q.get('hsnCode') || '',
      brand: q.get('brand') || '',
    },
  };
};

function ProductManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const initial = fromUrl(location.search);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0, published: 0, outOfStock: 0, lowStock: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initial.search);
  const [debounced, setDebounced] = useState(initial.search);
  const [filters, setFilters] = useState(initial.filters);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const seq = useRef(0);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [decision, setDecision] = useState(null); // { product, status }
  const [deciding, setDeciding] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [ioError, setIoError] = useState('');

  // follow links that change the query string (dashboard shortcuts, back button)
  useEffect(() => {
    const next = fromUrl(location.search);
    setSearch(next.search);
    setDebounced(next.search);
    setFilters(next.filters);
    setPage(1);
  }, [location.search]);

  // clear the "was added" message from history so a refresh does not repeat it
  useEffect(() => {
    if (location.state?.notice) navigate(location.pathname + location.search, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    getData('categories/list?limit=1000', null, authToken(), false).then((r) => setCategories(r?.categories || [])).catch(() => setCategories([]));
  }, []);
  useEffect(() => {
    if (!filters.categoryId) { setSubcategories([]); return; }
    getData(`subcategories/list?limit=1000&category_id=${filters.categoryId}`, null, authToken(), false).then((r) => setSubcategories(r?.subcategories || [])).catch(() => setSubcategories([]));
  }, [filters.categoryId]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const [sortBy, sortOrder] = filters.sort.split(':');
      const { sort, ...rest } = filters;
      const params = { page, limit: LIMIT, search: debounced, sortBy, sortOrder, ...rest };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('product/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(Array.isArray(res?.products) ? res.products : []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load products.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v, ...(k === 'categoryId' ? { subcategoryId: '' } : {}) })); setPage(1); };
  const pick = (k, v) => setF(k, filters[k] === v ? '' : v);
  const moreCount = ['brand', 'upc', 'hsnCode', 'minPrice', 'maxPrice', 'ownership'].filter((k) => filters[k]).length;
  const dirty = !!search || Object.entries(filters).some(([k, v]) => v && !(k === 'sort' && v === 'createdAt:desc'));
  const reset = () => { setSearch(''); setDebounced(''); setFilters(EMPTY); setPage(1); if (location.search) navigate('/productmanagement', { replace: true }); };

  const togglePublished = async (p) => {
    const next = !p.isPublished;
    setRows((list) => list.map((x) => (x._id === p._id ? { ...x, isPublished: next } : x)));
    try {
      const body = new FormData();
      body.append('isPublished', next);
      await putData(`product/update/${p._id}`, body, authToken(), false);
      load();
    } catch { load(); }
  };

  const decide = async () => {
    setDeciding(true);
    try {
      const body = new FormData();
      body.append('status', decision.status);
      await putData(`product/update/${decision.product._id}`, body, authToken(), false);
      setNotice(decision.status === 'approved' ? `“${decision.product.title}” is approved.` : `“${decision.product.title}” was rejected.`);
      setDecision(null);
      load();
    } catch { /* the service shows the error */ } finally {
      setDeciding(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteData(`product/delete/${toDelete._id}`, authToken());
      setToDelete(null);
      setNotice('Product deleted.');
      if (rows.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the product.');
    } finally {
      setDeleting(false);
    }
  };

  const runImport = async () => {
    setImporting(true);
    setImportErrors([]);
    setIoError('');
    try {
      const body = new FormData();
      body.append('file', importFile);
      const res = await fetch(`${API_URL}product/import`, { method: 'POST', headers: { Authorization: authToken() }, body });
      const result = await res.json().catch(() => ({}));
      if (res.status === 207 || !result.success) {
        setImportErrors(result.errors || [result.message || 'The file could not be imported.']);
      } else {
        setImportOpen(false);
        setImportFile(null);
        setNotice('Products imported.');
        load();
      }
    } catch (err) {
      setIoError(err?.message || 'The import failed.');
    } finally {
      setImporting(false);
    }
  };

  const doDownload = async (path, name) => {
    setIoError('');
    try { await download(path, name); } catch (err) { setIoError(err?.message || 'The download failed.'); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Products</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Your catalogue: prices, stock and what is live on the storefront.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
          <Button startIcon={<MdDownload />} onClick={() => doDownload('product/export', 'products_export.csv')} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>Export</Button>
          <Button startIcon={<MdUpload />} onClick={() => { setImportErrors([]); setImportFile(null); setImportOpen(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>Import</Button>
          <Button startIcon={<MdAdd />} variant="contained" onClick={() => navigate('add')} sx={yellowBtn}>Add product</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      {ioError && <Alert severity="error" onClose={() => setIoError('')} sx={{ mb: 2 }}>{ioError}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All products" value={stats.total} from="#6366f1" to="#4338ca" active={!filters.status && !filters.isPublished && !filters.stockStatus} onClick={() => { setFilters((f) => ({ ...f, status: '', isPublished: '', stockStatus: '' })); setPage(1); }} />
        <Kpi title="Needs approval" value={stats.pending} from="#f59e0b" to="#b45309" active={filters.status === 'pending'} onClick={() => pick('status', 'pending')} />
        <Kpi title="Live on storefront" value={stats.published} from="#22c55e" to="#15803d" active={filters.isPublished === 'true'} onClick={() => pick('isPublished', 'true')} />
        <Kpi title="Low stock" value={stats.lowStock} from="#fb923c" to="#c2410c" active={filters.stockStatus === 'low_stock'} onClick={() => pick('stockStatus', 'low_stock')} />
        <Kpi title="Out of stock" value={stats.outOfStock} from="#ef4444" to="#b91c1c" active={filters.stockStatus === 'out_of_stock'} onClick={() => pick('stockStatus', 'out_of_stock')} />
        <Kpi title="Rejected" value={stats.rejected} from="#94a3b8" to="#475569" active={filters.status === 'rejected'} onClick={() => pick('status', 'rejected')} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search name, UPC, product code, brand or HSN…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Category" value={filters.categoryId} onChange={(e) => setF('categoryId', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All categories</MenuItem>{categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Subcategory" value={filters.subcategoryId} disabled={!filters.categoryId} onChange={(e) => setF('subcategoryId', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All subcategories</MenuItem>{subcategories.map((c) => <MenuItem key={c._id} value={c._id}>{c.title}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filters.status} onChange={(e) => setF('status', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField select size="small" label="Live" value={filters.isPublished} onChange={(e) => setF('isPublished', e.target.value)} sx={{ minWidth: 110 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="true">Live</MenuItem><MenuItem value="false">Not live</MenuItem>
        </TextField>
        <TextField select size="small" label="Stock" value={filters.stockStatus} onChange={(e) => setF('stockStatus', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="in_stock">In stock</MenuItem><MenuItem value="low_stock">Low</MenuItem><MenuItem value="out_of_stock">Out of stock</MenuItem>
        </TextField>
        <Button startIcon={<FiFilter />} onClick={() => { setDraft(filters); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>More{moreCount ? ` (${moreCount})` : ''}</Button>
        {dirty && <Button onClick={reset} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Product</TableCell>
                <TableCell sx={head}>Category</TableCell>
                <TableCell sx={head} align="right">Price</TableCell>
                <TableCell sx={head}>Stock</TableCell>
                <TableCell sx={head} align="center">Live</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(7)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{dirty ? 'No products match these filters.' : 'No products yet. Add the first one.'}</TableCell></TableRow>
              )}
              {!loading && rows.map((p) => {
                const st = stockOf(p);
                const off = Number(p.b2c_mrp) > Number(p.b2c_sellingPrice) && p.b2c_sellingPrice ? Math.round(((p.b2c_mrp - p.b2c_sellingPrice) / p.b2c_mrp) * 100) : 0;
                const pending = p.status === 'pending';
                return (
                  <TableRow key={p._id} hover>
                    <TableCell sx={{ ...cell, maxWidth: 320 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar variant="rounded" src={img(p.product_images?.[0])} sx={{ width: 46, height: 46, bgcolor: '#e0e7ff', color: '#3730a3', border: '1px solid #e2e8f0' }}><MdInventory2 size={20} /></Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap onClick={() => navigate(`view/${p._id}`)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }} title={p.title}>{p.title}</Typography>
                          <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{[p.brand, p.productCode, p.upc && `UPC ${p.upc}`].filter(Boolean).join(' · ')}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cell}>{p.category_id?.title || '—'}<Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>{p.subcategory_id?.title || ''}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{p.isFree ? 'Free gift' : inr(p.b2c_sellingPrice)}</Typography>
                      {!p.isFree && p.b2c_mrp ? <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}><span style={{ textDecoration: off ? 'line-through' : 'none' }}>{inr(p.b2c_mrp)}</span>{off ? <b style={{ color: '#b45309', marginLeft: 6 }}>{off}% off</b> : null}</Typography> : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={st.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: st.bg, color: st.fg }} />
                      {(p.minimumStockQuantity || 0) > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>alert at {p.minimumStockQuantity}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={p.status !== 'approved' ? 'Only approved products can go live' : ''}><span><Switch size="small" checked={!!p.isPublished} disabled={p.status !== 'approved'} onChange={() => togglePublished(p)} /></span></Tooltip>
                    </TableCell>
                    <TableCell><StatusChip status={p.status} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {pending && <Tooltip title="Approve"><IconButton size="small" onClick={() => setDecision({ product: p, status: 'approved' })} sx={iconBtn('#16a34a')}><MdCheckCircle size={18} /></IconButton></Tooltip>}
                      {pending && <Tooltip title="Reject"><IconButton size="small" onClick={() => setDecision({ product: p, status: 'rejected' })} sx={iconBtn('#ea580c')}><MdBlock size={17} /></IconButton></Tooltip>}{pending ? ' ' : ''}
                      <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`view/${p._id}`)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`edit/${p._id}`)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(p); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} product{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, pg) => setPage(pg)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* more filters */}
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 360 }, p: 3 } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 2 }}>More filters</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField size="small" label="Brand" value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} />
          <TextField size="small" label="UPC" value={draft.upc} onChange={(e) => setDraft({ ...draft, upc: e.target.value })} />
          <TextField size="small" label="HSN code" value={draft.hsnCode} onChange={(e) => setDraft({ ...draft, hsnCode: e.target.value })} />
          <TextField select size="small" label="Owner" value={draft.ownership} onChange={(e) => setDraft({ ...draft, ownership: e.target.value })}>
            <MenuItem value="">Anyone</MenuItem><MenuItem value="Own Product">Our products</MenuItem><MenuItem value="Seller Product">Seller products</MenuItem>
          </TextField>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
            <TextField size="small" type="number" label="Price from (₹)" value={draft.minPrice} onChange={(e) => setDraft({ ...draft, minPrice: e.target.value })} />
            <TextField size="small" type="number" label="Price to (₹)" value={draft.maxPrice} onChange={(e) => setDraft({ ...draft, maxPrice: e.target.value })} />
          </Box>
          <TextField select size="small" label="Sort by" value={draft.sort} onChange={(e) => setDraft({ ...draft, sort: e.target.value })}>
            {SORTS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button fullWidth onClick={() => { setFilters((f) => ({ ...f, brand: '', upc: '', hsnCode: '', ownership: '', minPrice: '', maxPrice: '', sort: 'createdAt:desc' })); setPage(1); setDrawer(false); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Reset</Button>
          <Button fullWidth variant="contained" onClick={() => { setFilters((f) => ({ ...f, brand: draft.brand, upc: draft.upc, hsnCode: draft.hsnCode, ownership: draft.ownership, minPrice: draft.minPrice, maxPrice: draft.maxPrice, sort: draft.sort })); setPage(1); setDrawer(false); }} sx={yellowBtn}>Apply</Button>
        </Box>
      </Drawer>

      {/* approve / reject */}
      <Dialog open={!!decision} onClose={() => !deciding && setDecision(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>{decision?.status === 'approved' ? 'Approve this product?' : 'Reject this product?'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{decision?.product?.title}</b>{decision?.status === 'approved' ? ' becomes available to go live on the storefront.' : ' stays hidden from the storefront.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDecision(null)} disabled={deciding} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={decide} disabled={deciding} variant="contained" sx={decision?.status === 'approved' ? yellowBtn : { textTransform: 'none', fontWeight: 800, background: '#ea580c', '&:hover': { background: '#c2410c' } }}>{deciding ? 'Saving…' : decision?.status === 'approved' ? 'Approve' : 'Reject'}</Button>
        </DialogActions>
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete product?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            <b>{toDelete?.title}</b> is removed for good (a copy is archived), along with seller assignments and requests for it. It cannot be deleted while stock remains or while it is part of a combo, BOGO offer or variation group. You can switch it off instead.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* import */}
      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>Import products</Typography>
          <IconButton onClick={() => setImportOpen(false)} disabled={importing} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>Upload an .xlsx or .csv file to add many products at once. Start from the template so the columns match.</Typography>
          <Button onClick={() => doDownload('product/template', 'products_template.xlsx')} startIcon={<MdDownload />} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>Download template</Button>
          <Button component="label" sx={{ textTransform: 'none', fontWeight: 700, border: '2px dashed #cbd5e1', borderRadius: '12px', py: 2.5, color: '#475569' }}>
            {importFile ? importFile.name : 'Choose the file'}<input hidden type="file" accept=".csv,.xlsx" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportErrors([]); }} />
          </Button>
          {importErrors.length > 0 && (
            <Alert severity="error">
              <b>Fix these and try again:</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, maxHeight: 180, overflowY: 'auto' }}>{importErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportOpen(false)} disabled={importing} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={runImport} disabled={!importFile || importing} variant="contained" sx={yellowBtn}>{importing ? 'Uploading…' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductManagement;
